import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

import { AppFacadeService } from '@services/app-facade.service';
import { catchError, finalize, debounce } from 'rxjs/operators';
import { AuthService } from '@services/auth/auth.service';
import { BusinessFacadeService } from '@services/business/business-facade.service';
import { AdminFacadeService } from '@services/admin-facade.service';
import { ResellerModel, BusinessModel, UserModel } from '@models/index';
import { emptyResellerModel, emptyBusinessModel, emptyUserModel } from '@utilities/data-utils';
import { ErrorsEnum } from '@enums/errors.enum'
import { environment } from '@environments/environment';

import Swal from 'sweetalert2';
import { RouterFacadeService } from '@services/router-facade.service';
import { AuthFacadeService } from '@services/auth/auth-facade.service';

/**
 * Handles all API HTTP requests, adds 
 * resellerId, businessId,userId,role
 * version, typeApp headers to them and 
 * controls petition errors to decide if the
 * current user should be logged out or
 * not.
 */

@Injectable({
    providedIn: 'root'
})
export class ValidateOperationInterceptor implements HttpInterceptor {
    /**
     * Variable where the current selected reseller is stored
     */
    selectedReseller: ResellerModel = emptyResellerModel();
    /**
     * Variable where the current selected business is stored
     */
    selectedBusiness: BusinessModel = emptyBusinessModel();
    /**
     * Variable where the current logged user is stored
     */
    loggedUser: UserModel = emptyUserModel();

    /**
     * ValidateOperationInterceptor Constructor
     * - Subscribes to the app store to get the current selected reseller, current selected business and current logged user.
     * @param appFacadeService The Facade service of the app for storaging app errors and handling preloader functionality.
     * @param businessFacadeService Service used to get the current business.
     * @param adminFacadeService Service used to get the current reseller.
     * @param authFacadeService Service used to dispatch the logout action.
     * @param routerFacadeService Service used to redirect to auth or admin business dashboard view.
     */

    constructor(
        private appFacadeService: AppFacadeService,
        private businessFacadeService: BusinessFacadeService,
        private adminFacadeService: AdminFacadeService,
        private authService: AuthService,
        private authFacadeService: AuthFacadeService,
        private routerFacadeService: RouterFacadeService
    ) {
        console.log('validateOperation constructor');

        this.adminFacadeService.getSelectedReseller$().subscribe(reseller => {
            this.selectedReseller = reseller;
        });

        this.businessFacadeService.getSelectedBusiness$().subscribe(business => {
            this.selectedBusiness = business;
        });

        this.appFacadeService.getLoggedUser$().subscribe(user => {
            console.log('loggedUser:', user);
            this.loggedUser = user;
        });
    }

    /**
     * This method performs two functionalities:
     * - Catches API petition errors. Currently, if the error reason is 'LOGOUT', then the logout action is dispatched and 
     * redirected to the login view, but if the error reason is 'LOGIN', then it is redirected to '/admin-business/business'.
     * @param request
     * @param next 
     */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        request = request.clone({
            headers: request.headers
                .append('resellerId', `${this.selectedReseller.id}`)
                .append('businessId', `${this.selectedBusiness.id}`)
                .append('userId', `${this.loggedUser.id}`)
                .append('role', `${this.loggedUser.role.name ? this.loggedUser.role.name : ""}`)
                .append('version', environment.version)
                .append('typeApp', environment.typeApp)
        });

        return next.handle(request)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    if (request.url.includes('locations/search')) {
                        return next.handle(request);
                    }

                    if (error.status == ErrorsEnum.UNAUTHORIZED_USER_ERROR) {
                        const data = {
                            reason: error.error.reason ? error.error.reason : '',
                            status: error.status,
                            isLogOut: error.error.isLogOut,
                            message: error.error.message ? error.error.message : '',
                        };

                        if (data.reason == "LOGOUT") {
                            Swal.fire({
                                title: 'Error',
                                text: data.message,
                                icon: 'warning',
                                showCancelButton: false,
                                confirmButtonText: 'OK'
                            }).then(_ => {
                                this.authFacadeService.logOut();
                                this.routerFacadeService.gotoAuth$();
                            })
                        }

                        if (data.reason == "LOGOUT_V") {
                            Swal.fire({
                                title: 'Error',
                                text: data.message,
                                icon: 'warning',
                                showCancelButton: false,
                                confirmButtonText: 'OK'
                            }).then(_ => {
                                this.authFacadeService.logOut();
                                this.routerFacadeService.gotoAuth$();
                                window.location.reload()
                            })
                        }

                        if (data.reason == "LOGOUT_V") {
                            Swal.fire({
                                title: 'Error',
                                text: data.message,
                                icon: 'warning',
                                showCancelButton: false,
                                confirmButtonText: 'OK'
                            }).then(_ => {
                                this.authFacadeService.logOut();
                                this.routerFacadeService.gotoAuth$();
                                window.location.reload()
                            })
                        }

                        if (data.reason == "LOGIN") {
                            Swal.fire({
                                title: 'Error',
                                text: data.message,
                                icon: 'warning',
                                showCancelButton: false,
                                confirmButtonText: 'OK'
                            }).then(_ => {
                                this.routerFacadeService.navigateTo$("/admin-business/business")
                            })
                        }

                        if (data.reason == "TRIALOVER") {
                            this.appFacadeService.setErrorTrialOver()
                        }

                        console.warn("unauthorized user error:", error);
                        return next.handle(request);
                    }
                    return throwError(() => error);
                })
            );
    }
}
